import { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, Box, Button, CircularProgress, FormControl, FormControlLabel,
  IconButton, InputLabel, MenuItem, Select, Switch, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';

import { useIsMobile } from '../../hooks/use-is-mobile';
import { useAllClubs } from '../../hooks/use-all-clubs';
import { useAuthContext } from '../../context/auth-context';
import { useGetAllAffiliationsQuery } from '../../redux/services/affiliation-api';
import { imprimirDocumento } from '../../utils/print-document';
import { construirHojaEtiquetas, construirPrevisualizacion, POR_HOJA } from './qr-label';

const zonaDe = club => (typeof club.affiliatedTo === 'object' ? club.affiliatedTo?.zone ?? '' : '');
const afiliacionDe = club => (typeof club.affiliatedTo === 'object' ? club.affiliatedTo?._id : club.affiliatedTo);

const plural = (n, singular, plural_) => `${n} ${n === 1 ? singular : plural_}`;

/**
 * Genera e imprime las etiquetas QR de los clubes del campamento activo.
 *
 * Trae el listado completo —no la página que se ve en la tabla— porque las etiquetas se
 * imprimen de una vez para todo el campamento o para una zona entera; imprimir "lo que
 * cabía en pantalla" sería una trampa silenciosa.
 *
 * La impresión sale en una ventana aparte con su propio documento: el `@page` y la
 * retícula en milímetros no conviven con el layout del panel.
 */
export function QrLabelsDialog({ open, onClose }) {
  const isMobile = useIsMobile();
  const { activeCamp, hasPermission, effectiveZone } = useAuthContext();
  const campId = activeCamp?._id ?? null;

  const { data: affiliations = [] } = useGetAllAffiliationsQuery(campId ? { campId } : undefined, { skip: !campId });
  const { clubs, cargando, error } = useAllClubs(open, campId);

  const [zona, setZona] = useState('');
  const [incluirInactivos, setIncluirInactivos] = useState(false);
  const [preview, setPreview] = useState('');
  const [errorImpresion, setErrorImpresion] = useState('');

  useEffect(() => {
    if (!open) {
      setZona('');
      setIncluirInactivos(false);
      setPreview('');
      setErrorImpresion('');
    }
  }, [open]);

  /**
   * Un Secretario de Zona solo recibe los clubes de su zona, así que el desplegable se
   * fija en ella: ofrecerle las demás zonas del campamento sería ofrecerle imprimir
   * hojas vacías. Mismo criterio que el servidor (`zoneScope`) y que `AffiliationFilter`.
   */
  const zonaFija = Boolean(effectiveZone) && !hasPermission('reports.view-all-zones');
  const zoneOptions = useMemo(
    () => (zonaFija ? affiliations.filter(a => a.zone === effectiveZone) : affiliations),
    [affiliations, zonaFija, effectiveZone]
  );
  const zonaUnica = zonaFija && zoneOptions.length === 1 ? zoneOptions[0]._id : null;

  useEffect(() => {
    if (open && zonaUnica && zona !== zonaUnica) setZona(zonaUnica);
  }, [open, zonaUnica, zona]);

  // Por zona y, dentro de cada zona, alfabético: así se reparten hojas enteras por zona
  // sin recortar. El backend ya los devuelve ordenados por nombre.
  const seleccionados = useMemo(() => {
    const filtrados = clubs.filter(
      club =>
        (!zona || afiliacionDe(club) === zona) &&
        (incluirInactivos || club.status !== false)
    );
    return [...filtrados].sort(
      (a, b) => zonaDe(a).localeCompare(zonaDe(b), 'es') || a.name.localeCompare(b.name, 'es')
    );
  }, [clubs, zona, incluirInactivos]);

  useEffect(() => {
    if (!open || !activeCamp || seleccionados.length === 0) {
      setPreview('');
      return undefined;
    }
    let cancelado = false;
    construirPrevisualizacion({ camp: activeCamp, club: seleccionados[0] })
      .then(html => {
        if (!cancelado) setPreview(html);
      })
      .catch(() => {
        if (!cancelado) setPreview('');
      });
    return () => {
      cancelado = true;
    };
  }, [open, activeCamp, seleccionados]);

  const imprimir = async () => {
    setErrorImpresion('');
    try {
      const documento = await construirHojaEtiquetas({ camp: activeCamp, clubs: seleccionados });
      if (!imprimirDocumento(documento)) {
        setErrorImpresion('El navegador bloqueó la ventana de impresión. Permite las ventanas emergentes e inténtalo de nuevo.');
      }
    } catch {
      setErrorImpresion('No se pudieron generar las etiquetas.');
    }
  };

  const hojas = Math.ceil(seleccionados.length / POR_HOJA);
  const inactivosOcultos = clubs.filter(c => c.status === false).length;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1, fontWeight: 700 }}
      >
        Etiquetas QR
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Una etiqueta por club para pegar en su entrada. El juez la escanea desde el móvil y salta
          directo a ese club.
        </Typography>

        {!activeCamp && <Alert severity="info">Selecciona un campamento primero.</Alert>}

        {activeCamp && !activeCamp.logoUrl && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Este campamento no tiene logo, así que el QR sale sin logo al centro. Puedes subirlo
            desde Campamentos.
          </Alert>
        )}

        {(error || errorImpresion) && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error || errorImpresion}
          </Alert>
        )}

        {activeCamp && (
          <>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Zona</InputLabel>
                <Select
                  value={zona}
                  label="Zona"
                  onChange={e => setZona(e.target.value)}
                  disabled={zonaFija}
                >
                  {(!zonaFija || zoneOptions.length !== 1) && <MenuItem value="">Todas</MenuItem>}
                  {zoneOptions.map(a => (
                    <MenuItem key={a._id} value={a._id}>
                      {a.zone || a.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={incluirInactivos}
                    onChange={e => setIncluirInactivos(e.target.checked)}
                  />
                }
                label={
                  <Typography variant="body2">
                    Incluir inactivos{inactivosOcultos ? ` (${inactivosOcultos})` : ''}
                  </Typography>
                }
              />
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {cargando
                ? 'Cargando clubes…'
                : `${plural(seleccionados.length, 'club', 'clubes')} · ${plural(hojas, 'hoja', 'hojas')} A4`}
            </Typography>

            {cargando && <CircularProgress size={20} />}

            {!cargando && seleccionados.length === 0 && (
              <Typography variant="body2" color="text.disabled">
                No hay clubes que coincidan con los filtros.
              </Typography>
            )}

            {!cargando && preview && (
              <Box>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                  Vista previa
                </Typography>
                <Box
                  sx={{ maxWidth: 240, '& .label': { height: 'auto' } }}
                  dangerouslySetInnerHTML={{ __html: preview }}
                />
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', color: 'text.secondary' }}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          startIcon={<PrintIcon />}
          onClick={imprimir}
          disabled={cargando || seleccionados.length === 0}
          sx={{ bgcolor: '#0F6E56', '&:hover': { bgcolor: '#0a5a45' }, textTransform: 'none' }}
        >
          Imprimir
        </Button>
      </DialogActions>
    </Dialog>
  );
}
